"use client";

import { useAppContext } from "@/context/AppContext";
import { useSessionContext } from "@/context/SessionContext";
import { useWatchDogContext } from "@/context/WatchDogContext";
import { Log, ReportEmail, TestReport } from "@/types";
import {
  deleteLogs,
  deleteReports,
  fetchAllUsers,
  fetchLogs,
  fetchReports,
  fetchTestReports,
  switchUser,
} from "@/utils";
import { ADMIN_ITEMS } from "@/utils/HelperConstants";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type AdminDataType = "LOGS" | "REPORTS" | "TEST_REPORTS";

function readAdminList<T>(response: { status: number; data: unknown }): T[] {
  const body = response.data;
  const envelope =
    typeof body === "object" && body !== null
      ? (body as { success?: unknown; data?: unknown; message?: unknown })
      : null;

  if (
    response.status < 200 ||
    response.status >= 300 ||
    envelope?.success === false ||
    !Array.isArray(envelope?.data)
  ) {
    throw new Error(
      typeof envelope?.message === "string" && envelope.message.trim()
        ? envelope.message
        : `Invalid admin API list response (HTTP ${response.status})`,
    );
  }

  return envelope.data as T[];
}

type AdminContextType = {
  ADMIN_ITEMS: any[];
  emailList: string[];
  switchToUser: (email: string) => Promise<void>;
  isAdmin: boolean;
  logs: Log[];
  reports: ReportEmail[];
  testReports: TestReport[];
  deleteReportsAndLogs: (type: "LOGS" | "REPORTS") => void;
  refreshAdminData: (type: AdminDataType) => Promise<void>;
  selectedReportFilter: string;
  setSelectedReportFilter: (value: string) => void;
};

const defaultContext: AdminContextType = {
  ADMIN_ITEMS: [],
  emailList: [],
  switchToUser: async (email: string) => {},
  isAdmin: false,
  logs: [],
  reports: [],
  testReports: [],
  deleteReportsAndLogs: () => {},
  refreshAdminData: async () => {},
  selectedReportFilter: "ALL",
  setSelectedReportFilter: () => {},
};

const AdminContext = createContext<AdminContextType>(defaultContext);

export const AdminProvider = ({ children }: { children: ReactNode }) => {
  const { userInfoGlobal, setUserInfoGlobal, setErrorMessage, setIsLoading } =
    useAppContext();
  const { onLoginSuccess } = useSessionContext();
  const { stopRequestedRef } = useWatchDogContext();

  const [logs, setLogs] = useState(defaultContext.logs);
  const [reports, setReports] = useState(defaultContext.reports);
  const [testReports, setTestReports] = useState(defaultContext.testReports);
  const [selectedReportFilter, setSelectedReportFilter] = useState(
    defaultContext.selectedReportFilter,
  );

  const isAdmin = useMemo(() => {
    return userInfoGlobal?.role === "ADMIN";
  }, [userInfoGlobal]);

  useEffect(() => {
    const initApp = async () => {
      await getAllUsers();
      await refreshAdminData("LOGS");
      await refreshAdminData("TEST_REPORTS");
      await refreshAdminData("REPORTS");
    };
    if (isAdmin) {
      initApp();
    }
  }, [isAdmin]);

  const [emailList, setEmailList] = useState<string[]>([]);

  const getAllUsers = async () => {
    if (!userInfoGlobal?.token) return;

    try {
      const response = await fetchAllUsers(
        userInfoGlobal,
        userInfoGlobal.token,
      );

      if (response.status === 200) {
        const emails = readAdminList<{ client: { email: string } }>(
          response,
        ).map((user) => user.client.email);
        setEmailList(emails);
      }
    } catch (err) {
      setErrorMessage(String(err));
    }
  };

  const switchToUser = async (email: string) => {
    if (!userInfoGlobal?.token) return;
    setIsLoading(true);
    try {
      const payload = {
        email: userInfoGlobal.email,
        token: userInfoGlobal.token,
        data: {
          email: email,
        },
      };
      const response = await switchUser(payload, userInfoGlobal.token);

      if (response.status === 200) {
        stopRequestedRef.current = false;
        setUserInfoGlobal(response.data.data);
        await onLoginSuccess(response.data.data, false);
      }
    } catch (err) {
      setErrorMessage(String(err));
    }
    setIsLoading(false);
  };

  const refreshAdminData = async (type: AdminDataType) => {
    if (!userInfoGlobal?.token) return;

    try {
      let response;
      if (type === "LOGS") {
        response = await fetchLogs(userInfoGlobal!, userInfoGlobal!.token);
        const logs = readAdminList<Log>(response);
        setLogs(logs);
      } else if (type === "REPORTS") {
        response = await fetchReports(userInfoGlobal!, userInfoGlobal!.token);
        const reports = readAdminList<ReportEmail>(response);
        setReports(reports);
      } else if (type === "TEST_REPORTS") {
        response = await fetchTestReports(
          userInfoGlobal!,
          userInfoGlobal!.token,
        );
        const testReports = readAdminList<TestReport>(response);
        setTestReports(testReports);
        return;
      }
    } catch (err) {
      setErrorMessage(String(err));
    }
  };

  const deleteReportsAndLogs = async (type: "LOGS" | "REPORTS") => {
    try {
      setIsLoading(true);
      let response;
      if (type === "LOGS") {
        response = await deleteLogs(userInfoGlobal!, userInfoGlobal!.token);
        setIsLoading(false);
        if (response.status === 200) {
          setLogs([]);
        }
      }
      if (type === "REPORTS") {
        const payload = {
          email: userInfoGlobal!.email,
          token: userInfoGlobal!.token,
          data: {
            type: selectedReportFilter,
          },
        };
        response = await deleteReports(payload, userInfoGlobal!.token);
        setIsLoading(false);
        if (response.status === 200) {
          await refreshAdminData("REPORTS");
        }
      } else {
        return;
      }
    } catch (err) {
      setErrorMessage(String(err));
    }
  };

  return (
    <AdminContext.Provider
      value={{
        ADMIN_ITEMS,
        emailList,
        switchToUser,
        isAdmin,
        logs,
        reports,
        testReports,
        deleteReportsAndLogs,
        refreshAdminData,
        selectedReportFilter,
        setSelectedReportFilter,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};

export const useAdminContext = () => useContext(AdminContext);
